import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ReactComponent as CookieIcon } from '@/img/icons/cookie-icon.svg';
import {
    acceptCookieConcent,
    hasAcceptedCookieConcent,
    isExternalCookieConcentUrl,
    resolveCookieConcentUrl,
    shouldShowCookieConcent,
} from 'arbitrip-common/client/utils/CookieConcent';

const COOKIE_CONCENT_OFFSET_CSS_VARIABLE = '--cookie-concent-offset';

function clearCookieConcentOffset() {
    if (typeof document === 'undefined') {
        return;
    }

    document.documentElement.style.setProperty(COOKIE_CONCENT_OFFSET_CSS_VARIABLE, '0px');
}

function updateCookieConcentOffset(element) {
    if (typeof document === 'undefined' || !element) {
        return;
    }

    const nextHeight = Math.max(0, Math.ceil(element.getBoundingClientRect().height));
    document.documentElement.style.setProperty(COOKIE_CONCENT_OFFSET_CSS_VARIABLE, `${nextHeight}px`);
}

function CookieConcentBanner({ profile }) {
    const { t, i18n } = useTranslation();
    const descriptionId = useId();
    const bannerRef = useRef(null);
    const [dismissed, setDismissed] = useState(() => hasAcceptedCookieConcent());

    const resolvedUrl = useMemo(
        () =>
            resolveCookieConcentUrl({
                privacyPolicyUrl: t('PrivacyPolicyUrl', { defaultValue: '' }),
                termsFileUrl: profile?.terms_of_use?.file_url,
                termsAndConditionsUrl: t('TermsAndConditionsUrl', { defaultValue: '' }),
                currentOrigin: typeof window !== 'undefined' ? window.location.origin : '',
            }),
        [profile?.terms_of_use?.file_url, t],
    );

    const bannerEnabled = shouldShowCookieConcent(profile?.company?.settings, resolvedUrl);
    const showBanner = bannerEnabled && !dismissed;
    const isExternalLink = isExternalCookieConcentUrl(resolvedUrl);
    const cookieConcentConfig = profile?.cookie_concent;

    useEffect(() => {
        if (!bannerEnabled) {
            return;
        }

        setDismissed(hasAcceptedCookieConcent());
    }, [bannerEnabled]);

    useEffect(() => {
        if (!showBanner) {
            clearCookieConcentOffset();
            return;
        }

        const currentBannerElement = bannerRef.current;
        if (!currentBannerElement) {
            clearCookieConcentOffset();
            return;
        }

        const updateOffset = () => updateCookieConcentOffset(currentBannerElement);
        updateOffset();

        let resizeObserver;
        if (typeof ResizeObserver === 'function') {
            resizeObserver = new ResizeObserver(updateOffset);
            resizeObserver.observe(currentBannerElement);
        }

        window.addEventListener('resize', updateOffset);

        return () => {
            window.removeEventListener('resize', updateOffset);
            resizeObserver?.disconnect();
            clearCookieConcentOffset();
        };
    }, [showBanner]);

    if (!showBanner || !resolvedUrl) {
        return null;
    }

    return (
        <div className="cookie-concent-banner" dir={i18n.dir()} ref={bannerRef}>
            <div
                className="cookie-concent-banner__surface"
                role="dialog"
                aria-label={t('CookieConcentTitle')}
                aria-live="polite"
            >
                <div className="cookie-concent-banner__icon" aria-hidden="true">
                    <CookieIcon />
                </div>
                <div className="cookie-concent-banner__content" id={descriptionId}>
                    <Trans
                        i18nKey="CookieConcentMessage"
                        components={[
                            isExternalLink ? (
                                <a
                                    key="cookie-concent-flight-link"
                                    className="cookie-concent-banner__link"
                                    href={resolvedUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                />
                            ) : (
                                <a
                                    key="cookie-concent-flight-link"
                                    className="cookie-concent-banner__link"
                                    href={resolvedUrl}
                                />
                            ),
                            isExternalLink ? (
                                <a
                                    key="cookie-concent-hotel-link"
                                    className="cookie-concent-banner__link"
                                    href={resolvedUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                />
                            ) : (
                                <a
                                    key="cookie-concent-hotel-link"
                                    className="cookie-concent-banner__link"
                                    href={resolvedUrl}
                                />
                            ),
                        ]}
                    />
                </div>
                <button
                    className="cookie-concent-banner__button"
                    type="button"
                    data-testid="cookie-concent-close"
                    aria-describedby={descriptionId}
                    onClick={() => {
                        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
                        const accepted = acceptCookieConcent({
                            hostname,
                            allowedDomains: cookieConcentConfig?.allowed_domains,
                            cookieDomain: cookieConcentConfig?.cookie_domain,
                        });
                        if (!accepted) {
                            console.error(
                                new Error(
                                    'Cookie concent acceptance was rejected because the shared cookie could not be stored.',
                                ),
                            );
                            return;
                        }

                        setDismissed(true);
                    }}
                >
                    {t('Close')}
                </button>
            </div>
        </div>
    );
}

export default CookieConcentBanner;
